import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GridColumn } from 'src/components/GridWrapper';
import { MANAGE_SUPPLIERS_LAPTOP_MIN_WIDTH_PX } from 'src/constants/manageSuppliers';
import useWindowSize from 'src/hooks/useWindowSize';
import AttributesCell from 'src/sections/catalog_products/table_cell/AttributesCell';
import LastPurchasedInfoIcon from 'src/sections/catalog_products/table_cell/LastPurchasedInfoIcon';
import ShoppingCartAttributesFilter from 'src/sections/shopping_cart/table_cell/ShoppingCartAttributesFilter';
import ShoppingCartCatalogCell from 'src/sections/shopping_cart/table_cell/ShoppingCartCatalogCell';
import ShoppingCartDescriptionCell from 'src/sections/shopping_cart/table_cell/ShoppingCartDescriptionCell';
import ShoppingCartGlCodeCell from 'src/sections/shopping_cart/table_cell/ShoppingCartGlCodeCell';
import ShoppingCartLastPurchaseDateCell from 'src/sections/shopping_cart/table_cell/ShoppingCartLastPurchaseDateCell';
import ShoppingCartPriceCell from 'src/sections/shopping_cart/table_cell/ShoppingCartPriceCell';
import ShoppingCartQtyCell from 'src/sections/shopping_cart/table_cell/ShoppingCartQtyCell';
import ShoppingCartSwapItem from 'src/sections/shopping_cart/table_cell/ShoppingCartSwapItem';
import ShoppingCartTotalCell from 'src/sections/shopping_cart/table_cell/ShoppingCartTotalCell';
import ShoppingCartUomCell from 'src/sections/shopping_cart/table_cell/ShoppingCartUomCell';

const SHOPPING_CART_GRID_HORIZONTAL_PADDING_PX = 64;
const SHOPPING_CART_QTY_COLUMN_WIDTH_PX = 115;
const SHOPPING_CART_SWAP_ITEM_COLUMN_WIDTH_PX = 36;

const useShoppingCartColumns = () => {
  const { t } = useTranslation();
  const { width } = useWindowSize();

  const isCompactGrid = width > 0 && width < MANAGE_SUPPLIERS_LAPTOP_MIN_WIDTH_PX;

  const gridWidth = useMemo(() => {
    if (width <= 0) {
      return 1200;
    }

    if (isCompactGrid) {
      return Math.max(width - SHOPPING_CART_GRID_HORIZONTAL_PADDING_PX, 320);
    }

    return Math.max(width - 80, 1200);
  }, [isCompactGrid, width]);

  const setPercentage = useMemo(
    () => (percentage: number) => Math.round((gridWidth / 100) * percentage),
    [gridWidth]
  );

  const columnWidth = useMemo(
    () => (desktopPercentage: number, compactWidthPx: number) =>
      isCompactGrid ? compactWidthPx : setPercentage(desktopPercentage),
    [isCompactGrid, setPercentage]
  );

  const columns: GridColumn<Record<string, unknown>>[] = useMemo(
    () => [
      {
        field: 'selected',
        title: '',
        filter: 'boolean',
        menu: false,
        sortable: false,
      },
      {
        field: 'isNoItem',
        title: '',
        menu: false,
        sortable: false,
        width: SHOPPING_CART_SWAP_ITEM_COLUMN_WIDTH_PX,
        component: ShoppingCartSwapItem,
      },
      {
        field: 'qty',
        title: t('Qty'),
        sortable: false,
        filterable: false,
        menu: false,
        width: columnWidth(5, SHOPPING_CART_QTY_COLUMN_WIDTH_PX),
        minResizableWidth: SHOPPING_CART_QTY_COLUMN_WIDTH_PX,
        show: true,
        component: ShoppingCartQtyCell,
      },
      {
        field: 'attributes',
        title: t('Attributes'),
        filter: 'text',
        sortable: false,
        menu: true,
        width: columnWidth(7, 72),
        show: true,
        component: AttributesCell,
        columnMenu: ShoppingCartAttributesFilter,
      },
      {
        field: 'itemNum',
        title: t('Item #'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: columnWidth(6, 88),
        show: true,
      },
      {
        field: 'description',
        title: t('Description'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: columnWidth(16, 132),
        show: true,
        component: ShoppingCartDescriptionCell,
      },
      {
        field: 'supplierCompanyName',
        title: t('Supplier'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: columnWidth(9, 96),
        show: true,
      },
      {
        field: 'catalogName',
        title: t('Catalog'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: columnWidth(10, 92),
        show: true,
        component: ShoppingCartCatalogCell,
      },
      {
        field: 'weight',
        title: t('Wt.'),
        filter: 'text',
        dataType: 'string',
        sortable: true,
        menu: true,
        width: columnWidth(5, 56),
        show: true,
      },
      {
        field: 'uom',
        title: t('UOM'),
        filter: 'text',
        sortable: true,
        menu: true,
        width: columnWidth(4, 50),
        show: true,
        component: ShoppingCartUomCell,
      },
      {
        field: 'price',
        title: t('Price'),
        filter: 'numeric',
        format: '{0:c2}',
        filterFormat: 'c',
        dataType: 'dollar',
        sortable: true,
        menu: true,
        width: columnWidth(6, 72),
        show: true,
        component: ShoppingCartPriceCell,
      },
      {
        field: 'total',
        title: t('Total'),
        filter: 'numeric',
        format: '{0:c2}',
        filterFormat: 'c',
        dataType: 'dollar',
        sortable: true,
        menu: true,
        width: columnWidth(6, 72),
        show: true,
        component: ShoppingCartTotalCell,
      },
      {
        field: 'glCode',
        title: t('GL Code'),
        filter: 'boolean',
        menu: false,
        sortable: false,
        width: columnWidth(8, 78),
        show: true,
        headerCell: () => (
          <span
            className="k-column-title !whitespace-normal leading-tight"
            data-tooltip-id="global-tooltip"
            data-tooltip-content={t('GL Code')}
          >
            {t('GL Code')}
          </span>
        ),
        component: ShoppingCartGlCodeCell,
      },
      {
        field: 'lastPurchaseDate',
        headerClassName: 'cursor-pointer',
        headerCell: () => (
          <>
            <span
              className="k-column-title"
              data-tooltip-id="global-tooltip"
              data-tooltip-content={t('Shopping List Last Purchased')}
            >
              {t('Shopping List Last Purchased')}
            </span>
            <LastPurchasedInfoIcon />
          </>
        ),
        filter: 'date',
        dataType: 'date',
        sortable: true,
        menu: true,
        width: columnWidth(10, 110),
        // show: !isCompactGrid,
        component: ShoppingCartLastPurchaseDateCell,
      },
    ],
    [columnWidth, t]
  );

  return columns;
};

export default useShoppingCartColumns;
